import { FeatureFlag } from './useFeatureFlag';

/**
 * Puts the Intercom messenger on the page that calls it, and takes it away
 * again on leave.
 *
 * The messenger is scoped to the support pages rather than mounted globally.
 * That is not only about where the launcher appears: booting pulls in
 * `@intercom/messenger-js-sdk` and calls `GetFinToken`, a round trip that also
 * syncs the Intercom contact. None of that should happen because someone
 * loaded the homepage.
 *
 * The gate is unchanged from when this lived in the default layout —
 * authenticated (checked inside `bootIntercom`) and the `support-chat` flag —
 * with the route as a third condition.
 *
 * On leave the launcher is hidden rather than the messenger shut down, so a
 * customer who wanders off mid-conversation comes back to the same one. See
 * `setIntercomLauncherVisible`.
 */
export function useSupportChat(): void {
  const { hasAccessToFeature } = useFeatureFlag();
  const supportChatEnabled = hasAccessToFeature(FeatureFlag.SupportChat);

  // The flag is a ComputedRef that flips when the OpenFeature provider becomes
  // ready, so it is watched rather than read once.
  //
  // Leaving the page while the token request is in flight is handled inside
  // `setIntercomLauncherVisible`, which records the intent even before the
  // messenger has booted, so the boot itself starts hidden. `onPage` only
  // avoids a pointless `show` on top of that.
  let onPage = true;

  watch(
    supportChatEnabled,
    (enabled) => {
      if (!enabled) {
        // Revoked mid-session. The messenger cannot be un-booted without ending the conversation
        // (see setIntercomLauncherVisible), so hide the launcher instead of leaving it up.
        void setIntercomLauncherVisible(false);
        return;
      }

      // Declare the intent before booting, so a first boot comes up visible rather than starting
      // hidden and flashing in a moment later. A no-op until the messenger exists — its only job
      // here is to record what we want.
      void setIntercomLauncherVisible(true);

      void bootIntercom().then(() => {
        // Re-check the flag as well as the route: booting is asynchronous, and a revocation while
        // the token request was in flight must not be undone by its continuation.
        if (onPage && supportChatEnabled.value) {
          void setIntercomLauncherVisible(true);
        }
      });
    },
    { immediate: true },
  );

  onUnmounted(() => {
    onPage = false;
    void setIntercomLauncherVisible(false);
  });
}
